
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';

export default function Contact() {
  const benefits = [
    {
      icon: 'ri-stack-line',
      title: 'Built for Scale',
      description: 'Agencies use our built-in intelligent tools like best time to post and hashtag suggestions to manage more clients easily.',
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Agency-First Features',
      description: 'Get custom workflows, client approvals, and team collaboration tools made for agency teams.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'ROI You Can Show',
      description: 'Create beautiful, customizable reports that showcase your impact and help you win more work.',
    },
    {
      icon: 'ri-time-line',
      title: 'Time-Saving Automation',
      description: 'Save your social team hours every month with smarter scheduling and bulk uploads.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Premium Support',
      description: 'Need expert social strategy and support? Our dedicated agency service team is here to help.',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-mail-line mr-2"></i>
            Get In Touch
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            We'd Love to
            <span className="block text-[#38a3a5]">Hear From You</span>
          </h1>

          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Have a question about MandaPost? Our team is here to help you succeed with intelligent marketing automation.
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A] mb-1">&lt;24h</div>
              <p className="text-sm text-gray-600">Response Time</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A] mb-1">24/7</div>
              <p className="text-sm text-gray-600">Support Available</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A] mb-1">98%</div>
              <p className="text-sm text-gray-600">Satisfaction Rate</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-200">
              <div className="text-3xl font-bold text-[#22577A] mb-1">50K+</div>
              <p className="text-sm text-gray-600">Happy Customers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left - Contact Form (2 columns) */}
            <div className="lg:col-span-2">
              <ContactForm />
            </div>

            {/* Right - Contact Info (1 column) */}
            <div>
              <ContactInfo />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose MandaPost Section */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white font-medium mb-6">
              <i className="ri-star-line mr-2"></i>
              Why Choose Us
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose MandaPost?
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Built for professionals who need powerful tools, seamless workflows, and measurable results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border-2 border-white/20 hover:bg-white/20 hover:border-white/40 transition-all group"
              >
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${benefit.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-white/90 leading-relaxed text-lg">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* Additional CTA */}
          <div className="mt-16 text-center">
            <p className="text-white/90 text-lg mb-6">
              Join thousands of marketers, agencies, and businesses already using MandaPost to streamline their social media management.
            </p>
            <a
              href="/features"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
            >
              Explore All Features
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-question-line mr-2"></i>
              Common Questions
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                question: 'What is your response time?',
                answer: 'We typically respond to all inquiries within 24 hours during business days.',
              },
              {
                question: 'Do you offer phone support?',
                answer: 'Yes, phone support is available for Professional and Enterprise plans.',
              },
              {
                question: 'Can I schedule a demo?',
                answer: 'Absolutely! Contact our sales team to schedule a personalized demo.',
              },
              {
                question: 'Where are you located?',
                answer: 'Our headquarters is in San Francisco, with remote team members worldwide.',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all"
              >
                <h3 className="text-lg font-bold text-[#22577A] mb-3 flex items-start gap-2">
                  <i className="ri-question-mark text-[#66b3ff] mt-1"></i>
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <a
              href="#"
              className="inline-flex items-center gap-2 text-[#22577A] font-semibold hover:text-[#38a3a5] transition-colors text-lg"
            >
              View Full FAQ
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-rocket-line text-4xl text-white"></i>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start your 14-day free trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer"
            >
              <i className="ri-play-circle-line"></i>
              Start Free Trial
            </a>
            <a
              href="#"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all cursor-pointer"
            >
              <i className="ri-calendar-line"></i>
              Schedule Demo
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
